import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { consoleSocketUrl } from "@/lib/api";
import { queriesForRoute, queriesForTopic } from "@/lib/queryKeys";
import type { ScreenFrameHeader, WsServerMessage } from "@/types/fleet";
import { openMockSocket, MOCKS_ENABLED } from "@/mocks/socket";

/**
 * The console's single multiplexed WebSocket.
 *
 * Replaces the fork's `useLiveEvents`, which opened one socket per hook and
 * reconnected on a flat 3-second timer. Both were wrong here:
 *
 *  - one socket per hook means a device page with a health widget, a run tail
 *    and a summary badge holds three sockets, each re-authenticating;
 *  - a flat 3 s retry across several tabs behind one flapping uplink is a
 *    synchronised retry storm aimed at the machine that just fell over.
 *
 * So: one socket per tab, a refcounted topic registry over it, and exponential
 * backoff 1→2→4→8→15 s with ±20% jitter to break the lockstep.
 */

export type SocketStatus = "connecting" | "open" | "closed";

export type FrameHandler = (data: unknown, seq: number, topic: string) => void;
export type BinaryHandler = (frame: BinaryFrame) => void;

export interface BinaryFrame {
  header: ScreenFrameHeader | null;
  /** The JPEG payload, offset past the ICSF header. */
  bytes: Uint8Array;
  raw: ArrayBuffer;
}

export interface ClientMessage {
  t: "sub" | "unsub" | "action" | "stream" | "ping";
  topics?: string[];
  session_id?: string;
  device_id?: string;
  d?: unknown;
}

interface Sub {
  handler: FrameHandler;
}

interface TopicState {
  subs: Set<Sub>;
  /** Last `seq` seen on this topic; null until the first frame after a connect. */
  lastSeq: number | null;
}

interface ConsoleSocketApi {
  status: SocketStatus;
  /** Rises on every successful (re)connect; useful as an effect dependency. */
  generation: number;
  subscribe: (topic: string, handler: FrameHandler) => () => void;
  subscribeBinary: (topic: string, handler: BinaryHandler) => () => void;
  send: (msg: ClientMessage) => boolean;
  /** Topics currently held by at least one component. */
  topics: () => string[];
}

const Ctx = createContext<ConsoleSocketApi | null>(null);

/* ------------------------------------------------------------------ *
 * Backoff
 * ------------------------------------------------------------------ */

const BACKOFF_STEPS = [1_000, 2_000, 4_000, 8_000, 15_000];
const MAX_BACKOFF = 15_000;
/** No frame and no pong for this long ⇒ assume a black hole and reconnect. */
const LIVENESS_TIMEOUT_MS = 70_000;
const PING_INTERVAL_MS = 30_000;

function backoffDelay(attempt: number): number {
  const base = BACKOFF_STEPS[Math.min(attempt, BACKOFF_STEPS.length - 1)] ?? MAX_BACKOFF;
  // ±20% jitter. Four tabs behind one uplink must not all retry on the same tick.
  const jitter = 0.8 + Math.random() * 0.4;
  return Math.min(MAX_BACKOFF * 1.2, Math.round(base * jitter));
}

/* ------------------------------------------------------------------ *
 * ICSF binary frame (fleet-v1.md §7)
 * ------------------------------------------------------------------ */

export function parseScreenFrame(buf: ArrayBuffer): BinaryFrame {
  const raw = { header: null as ScreenFrameHeader | null, bytes: new Uint8Array(0), raw: buf };
  if (buf.byteLength < 12) return raw;
  const view = new DataView(buf);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== "ICSF") return raw;
  const version = view.getUint16(4, false);
  if (version !== 1) return raw;
  const headerLen = view.getUint32(8, false);
  if (12 + headerLen > buf.byteLength) return raw;
  const header = decodeHeader(buf, headerLen);
  return { header, bytes: new Uint8Array(buf, 12 + headerLen), raw: buf };
}

function decodeHeader(buf: ArrayBuffer, headerLen: number): ScreenFrameHeader | null {
  try {
    const text = new TextDecoder().decode(new Uint8Array(buf, 12, headerLen));
    return JSON.parse(text) as ScreenFrameHeader;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ *
 * Provider
 * ------------------------------------------------------------------ */

export function ConsoleSocketProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const location = useLocation();
  const [status, setStatus] = useState<SocketStatus>("connecting");
  const [generation, setGeneration] = useState(0);

  const socketRef = useRef<WebSocket | null>(null);
  const topicsRef = useRef<Map<string, TopicState>>(new Map());
  const binaryRef = useRef<Map<string, Set<BinaryHandler>>>(new Map());
  const attemptRef = useRef(0);
  const timerRef = useRef<number | null>(null);
  const pingRef = useRef<number | null>(null);
  // Seeded on connect, not at render time — reading the clock during render is
  // impure, and the value is meaningless until a socket is actually open.
  const lastMessageRef = useRef<number>(0);
  const connectedOnceRef = useRef(false);
  const stoppedRef = useRef(false);

  // The route is read through a ref so a navigation does not tear the socket
  // down and rebuild it; only the reconnect handler needs the current value.
  // Written in an effect, not during render — a render may be discarded, and a
  // ref written by a discarded render is a lie the next reconnect would act on.
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const rawSend = useCallback((msg: ClientMessage): boolean => {
    const ws = socketRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(msg));
    return true;
  }, []);

  /* ---------- frame dispatch ---------- */

  const handleFrame = useCallback(
    (topic: string, seq: number, data: unknown) => {
      const state = topicsRef.current.get(topic);
      if (!state) return;

      // Every topic frame carries a monotonic seq. A gap means we missed an
      // update, and the tail must never be trusted to have been gapless — so
      // refetch this topic's queries rather than rendering a hole.
      if (state.lastSeq !== null && seq !== state.lastSeq + 1) {
        if (seq > state.lastSeq) {
          for (const key of queriesForTopic(topic)) void qc.invalidateQueries({ queryKey: key });
        } else {
          // Out-of-order or replayed frame: ignore it, keep the high-water mark.
          return;
        }
      }
      state.lastSeq = seq;
      for (const sub of state.subs) {
        try {
          sub.handler(data, seq, topic);
        } catch (e) {
          console.error("[console-socket] topic handler threw", topic, e);
        }
      }
    },
    [qc],
  );

  const handleBinary = useCallback((buf: ArrayBuffer) => {
    const frame = parseScreenFrame(buf);
    // A binary frame carries no topic field — the server forwards the device's
    // bytes verbatim, and the ICSF header names a session/stream, not a device.
    // Phase 2 filters on `header.session_id`; here every binary subscriber sees
    // every frame and decides for itself. (ASSUMPTION, see handover notes.)
    for (const handlers of binaryRef.current.values()) {
      for (const h of handlers) {
        try {
          h(frame);
        } catch (e) {
          console.error("[console-socket] binary handler threw", e);
        }
      }
    }
  }, []);

  /* ---------- connect ---------- */

  // `connect` and `scheduleReconnect` are mutually recursive. The indirection
  // through a ref is what keeps that from being a use-before-declare hazard:
  // the timer resolves the *current* connect at fire time rather than closing
  // over whichever one existed when it was scheduled.
  const connectRef = useRef<() => void>(() => {});

  const scheduleReconnect = useCallback(() => {
    if (stoppedRef.current) return;
    if (timerRef.current !== null) return;
    const delay = backoffDelay(attemptRef.current);
    attemptRef.current += 1;
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      connectRef.current();
    }, delay);
  }, []);

  const connect = useCallback(() => {
    if (stoppedRef.current) return;
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    setStatus("connecting");
    let ws: WebSocket;
    try {
      ws = MOCKS_ENABLED
        ? (openMockSocket() as unknown as WebSocket)
        : new WebSocket(consoleSocketUrl());
    } catch {
      scheduleReconnect();
      return;
    }
    ws.binaryType = "arraybuffer";
    socketRef.current = ws;

    ws.onopen = () => {
      if (stoppedRef.current) {
        ws.close();
        return;
      }
      attemptRef.current = 0;
      lastMessageRef.current = Date.now();
      setStatus("open");

      // Re-send the whole topic set. The server holds no subscription state for
      // a socket that just died, so this is the only thing that makes the
      // registry true again.
      const topics = [...topicsRef.current.keys()];
      // seq restarts with the connection; forget the high-water marks so the
      // first frame after a reconnect is not mistaken for a gap.
      for (const st of topicsRef.current.values()) st.lastSeq = null;
      if (topics.length) ws.send(JSON.stringify({ t: "sub", topics }));

      const isReconnect = connectedOnceRef.current;
      connectedOnceRef.current = true;
      setGeneration((g) => g + 1);

      if (isReconnect) {
        // Only the page the operator is actually looking at. Invalidating
        // everything would have four idle tabs each fire a dozen requests the
        // moment a flapping uplink comes back — precisely when the server can
        // least afford it.
        for (const key of queriesForRoute(pathnameRef.current)) {
          void qc.invalidateQueries({ queryKey: key });
        }
      }
    };

    ws.onmessage = (ev: MessageEvent) => {
      lastMessageRef.current = Date.now();
      if (typeof ev.data !== "string") {
        if (ev.data instanceof ArrayBuffer) handleBinary(ev.data);
        else if (ev.data instanceof Blob) void ev.data.arrayBuffer().then(handleBinary);
        return;
      }
      let msg: WsServerMessage;
      try {
        msg = JSON.parse(ev.data) as WsServerMessage;
      } catch {
        return;
      }
      switch (msg.t) {
        case "frame":
          handleFrame(msg.topic, msg.seq, msg.d);
          break;
        case "error":
          console.warn("[console-socket] server error", msg.code, msg.message);
          break;
        case "result":
        case "pong":
          break;
      }
    };

    ws.onerror = () => {
      // onclose always follows; the reconnect is scheduled there so it happens
      // exactly once.
    };

    ws.onclose = () => {
      if (socketRef.current === ws) socketRef.current = null;
      setStatus("closed");
      scheduleReconnect();
    };
  }, [qc, handleFrame, handleBinary, scheduleReconnect]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  /* ---------- lifecycle ---------- */

  useEffect(() => {
    stoppedRef.current = false;
    connect();

    // Keep-alive plus a liveness check: a socket that stays "open" while the
    // network quietly ate every frame is worse than a closed one, because
    // nothing retries.
    pingRef.current = window.setInterval(() => {
      const ws = socketRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (lastMessageRef.current === 0) return;
      if (Date.now() - lastMessageRef.current > LIVENESS_TIMEOUT_MS) {
        ws.close();
        return;
      }
      ws.send(JSON.stringify({ t: "ping" }));
    }, PING_INTERVAL_MS);

    const onOnline = () => {
      // The uplink came back — do not sit out the remaining backoff.
      if (socketRef.current) return;
      attemptRef.current = 0;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      connect();
    };
    window.addEventListener("online", onOnline);

    return () => {
      stoppedRef.current = true;
      window.removeEventListener("online", onOnline);
      if (pingRef.current !== null) window.clearInterval(pingRef.current);
      if (timerRef.current !== null) window.clearTimeout(timerRef.current);
      timerRef.current = null;
      const ws = socketRef.current;
      socketRef.current = null;
      ws?.close();
    };
  }, [connect]);

  /* ---------- registry ---------- */

  const subscribe = useCallback(
    (topic: string, handler: FrameHandler) => {
      const map = topicsRef.current;
      let state = map.get(topic);
      if (!state) {
        state = { subs: new Set(), lastSeq: null };
        map.set(topic, state);
        rawSend({ t: "sub", topics: [topic] });
      }
      const sub: Sub = { handler };
      state.subs.add(sub);

      return () => {
        const st = topicsRef.current.get(topic);
        if (!st) return;
        st.subs.delete(sub);
        // Refcount hit zero: tell the server to stop sending, and forget the
        // seq so a later resubscribe starts clean.
        if (st.subs.size === 0) {
          topicsRef.current.delete(topic);
          rawSend({ t: "unsub", topics: [topic] });
        }
      };
    },
    [rawSend],
  );

  const subscribeBinary = useCallback((topic: string, handler: BinaryHandler) => {
    const map = binaryRef.current;
    let set = map.get(topic);
    if (!set) {
      set = new Set();
      map.set(topic, set);
    }
    set.add(handler);
    return () => {
      const s = binaryRef.current.get(topic);
      if (!s) return;
      s.delete(handler);
      if (s.size === 0) binaryRef.current.delete(topic);
    };
  }, []);

  const api = useMemo<ConsoleSocketApi>(
    () => ({
      status,
      generation,
      subscribe,
      subscribeBinary,
      send: rawSend,
      topics: () => [...topicsRef.current.keys()],
    }),
    [status, generation, subscribe, subscribeBinary, rawSend],
  );

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

/* ------------------------------------------------------------------ *
 * Hooks
 * ------------------------------------------------------------------ */

export function useConsoleSocket(): ConsoleSocketApi {
  const c = useContext(Ctx);
  if (!c) throw new Error("useConsoleSocket outside ConsoleSocketProvider");
  return c;
}

/**
 * Subscribe to one topic for as long as this component is mounted.
 *
 * `onFrame` may be an inline closure: it is kept in a ref, so changing it does
 * not resubscribe. Pass `null` as the topic to hold no subscription — that is
 * how a page waits for an id it does not have yet.
 */
export function useTopic(topic: string | null, onFrame: FrameHandler): void {
  const { subscribe } = useConsoleSocket();
  const ref = useRef(onFrame);
  // Updated in an effect rather than during render: a render React discards
  // must not leave a stale handler installed for the live subscription.
  useEffect(() => {
    ref.current = onFrame;
  }, [onFrame]);

  useEffect(() => {
    if (!topic) return;
    return subscribe(topic, (d, seq, t) => ref.current(d, seq, t));
  }, [topic, subscribe]);
}

/** Binary (screen) frames. Phase 1 registers nothing; phase 2 drives the canvas. */
export function useBinaryTopic(topic: string | null, onBinary: BinaryHandler): void {
  const { subscribeBinary } = useConsoleSocket();
  const ref = useRef(onBinary);
  useEffect(() => {
    ref.current = onBinary;
  }, [onBinary]);

  useEffect(() => {
    if (!topic) return;
    return subscribeBinary(topic, (f) => ref.current(f));
  }, [topic, subscribeBinary]);
}

/** Just the connection status, for the badge and for disabling live-only buttons. */
export function useSocketStatus(): SocketStatus {
  return useConsoleSocket().status;
}
